'use client';

import React, { useState, useEffect } from 'react';
import { FaCashRegister, FaMoneyBillWave, FaCalculator, FaChartLine, FaQuestionCircle, FaCheckCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { useTheme } from 'next-themes';

const CashBackCalculator = () => {
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [cashBackRate, setCashBackRate] = useState('');
  const [minSpend, setMinSpend] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState({});
  const { theme } = useTheme();

  useEffect(() => {
    if (purchaseAmount && cashBackRate) {
      const amount = parseFloat(purchaseAmount);
      const rate = parseFloat(cashBackRate);
      const cashBack = (amount * rate) / 100;
      
      setChartData({
        options: {
          chart: {
            type: 'donut',
            animations: {
              enabled: true,
              easing: 'easeinout',
              speed: 800
            }
          },
          colors: ['#4CAF50', '#E91E63', '#2196F3'],
          labels: ['Cash Back', 'Your Contribution', 'Total'],
          theme: {
            mode: theme === 'dark' ? 'dark' : 'light'
          },
          responsive: [{
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }]
        },
        series: [
          cashBack,
          amount - cashBack,
          amount
        ]
      });
    }
  }, [purchaseAmount, cashBackRate, theme]);

  const calculateCashBack = () => {
    if (!purchaseAmount || !cashBackRate) {
      alert('Please enter both purchase amount and cash back rate');
      return;
    }

    const amount = parseFloat(purchaseAmount);
    const rate = parseFloat(cashBackRate);
    
    if (isNaN(amount) || isNaN(rate)) {
      alert('Please enter valid numbers');
      return;
    }

    let cashBack = (amount * rate) / 100;
    
    if (minSpend && amount < parseFloat(minSpend)) {
      setResult(`You need to spend at least ₹${minSpend} to qualify for cash back`);
    } else {
      setResult(`You will earn ₹${cashBack.toFixed(2)} cash back`);
    }
  };

  const features = [
    {
      icon: <FaCheckCircle className="text-green-500" />,
      title: 'Instant Calculation',
      description: 'Get your cash back amount instantly with our fast calculator'
    },
    {
      icon: <FaChartLine className="text-blue-500" />,
      title: 'Visual Representation',
      description: 'See your cash back in a beautiful pie chart'
    },
    {
      icon: <FaMoneyBillWave className="text-yellow-500" />,
      title: 'Multiple Currencies',
      description: 'Support for multiple currencies and payment methods'
    }
  ];

  const faqs = [
    {
      question: 'What is cash back?',
      answer: 'Cash back is a reward program where you receive a percentage of your purchase amount back as cash.'
    },
    {
      question: 'How does this calculator work?',
      answer: 'Enter your purchase amount and cash back rate, and the calculator will show you how much cash back you can earn.'
    },
    {
      question: 'Is there a minimum spend requirement?',
      answer: 'Yes, some cash back programs have minimum spend requirements. Use the minimum spend field to check if you qualify.'
    }
  ];

  const howToUseSteps = [
    {
      step: 1,
      title: 'Enter Purchase Amount',
      description: 'Input your total purchase amount in the first field. This is the total amount you plan to spend.',
      icon: <FaMoneyBillWave className="text-green-500" />
    },
    {
      step: 2,
      title: 'Set Cash Back Rate',
      description: 'Enter the cash back percentage rate offered by your program. This is typically between 1-5%.',
      icon: <FaCalculator className="text-blue-500" />
    },
    {
      step: 3,
      title: 'Check Minimum Spend',
      description: 'If your program has a minimum spend requirement, enter it in the third field. This ensures you meet the eligibility criteria.',
      icon: <FaCashRegister className="text-yellow-500" />
    },
    {
      step: 4,
      title: 'Calculate Your Rewards',
      description: 'Click the "Calculate Cash Back" button to see your potential cash back amount and view the breakdown chart.',
      icon: <FaChartLine className="text-purple-500" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Cash Back Calculator - Calculate Your Cash Back Rewards</title>
        <meta name="description" content="Calculate your cash back rewards with our easy-to-use calculator. Enter your purchase amount and cash back rate to see how much you can earn." />
        <meta property="og:title" content="Cash Back Calculator" />
        <meta property="og:description" content="Calculate your cash back rewards with our easy-to-use calculator. Enter your purchase amount and cash back rate to see how much you can earn." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cash Back Calculator" />
        <meta name="twitter:description" content="Calculate your cash back rewards with our easy-to-use calculator. Enter your purchase amount and cash back rate to see how much you can earn." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Calculator Section */}
        <div className="calculator-header text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <FaCashRegister className="text-4xl text-green-500 dark:text-green-400" />
              <h2 className="text-2xl font-bold ml-3">Cash Back Calculator</h2>
            </div>

            <div className="space-y-6">
              <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                <FaMoneyBillWave className="text-green-500 dark:text-green-400" />
                <input
                  type="number"
                  placeholder="Enter purchase amount (₹)"
                  value={purchaseAmount}
                  onChange={(e) => setPurchaseAmount(e.target.value)}
                  className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                />
              </div>

              <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                <FaCalculator className="text-green-500 dark:text-green-400" />
                <input
                  type="number"
                  placeholder="Enter cash back rate (%)"
                  value={cashBackRate}
                  onChange={(e) => setCashBackRate(e.target.value)}
                  className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                />
              </div>

              <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                <FaMoneyBillWave className="text-green-500 dark:text-green-400" />
                <input
                  type="number"
                  placeholder="Minimum spend requirement (₹)"
                  value={minSpend}
                  onChange={(e) => setMinSpend(e.target.value)}
                  className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                />
              </div>

              <button
                onClick={calculateCashBack}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-red-500 dark:text-green-400 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition duration-300"
              >
                Calculate Cash Back
              </button>

              {result && (
                <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg mt-4">
                  <h3 className="font-semibold mb-2">Result:</h3>
                  <p className="text-green-800 dark:text-green-400 text-lg">{result}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Chart Section */}
        {Object.keys(chartData).length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">Cash Back Breakdown</h3>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <Chart
                options={chartData.options}
                series={chartData.series}
                type="donut"
                width={400}
              />
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
              >
                {feature.icon}
                <h4 className="mt-3 font-semibold">{feature.title}</h4>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* How to Use Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">How to Use</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howToUseSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="text-green-700 dark:text-green-400 font-bold">{step.step}</span>
                  </div>
                </div>
                <div className="text-center">
                  {step.icon}
                  <h4 className="mt-3 font-semibold">{step.title}</h4>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center">
                  <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                  <h4 className="font-semibold">{faq.question}</h4>
                </div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashBackCalculator;
